/**
 * The mention wire format used by comments.
 *
 * A comment is stored (and sent to the backend) as a plain string in which every mention is encoded
 * as `@[<display>](<id>)`, e.g. `Hey @[@John Doe](0f1e-...), can you check this?`. The display part
 * already contains the leading `@` because call sites pass a `formatMention` that adds it.
 *
 * This module converts between that markup and the plain text a normal `<textarea>` shows, which is
 * what `react-mentions` used to do for us. It is the only place that knows the format, apart from
 * {@link parseCommentString}, which reads it back when rendering a comment.
 */

/** Matches a single mention token. Kept in sync with the mention matcher in `parseCommentString`. */
const MENTION_TOKEN = /@\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Matches a mention trigger directly in front of the caret: an `@` at the start of the text or after
 * whitespace, followed by the query. The query cannot contain whitespace or another `@`, mirroring
 * the trigger `react-mentions` used.
 */
const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/;

export type MentionQuery = {
    /** Index of the `@` in the plain text. */
    start: number;
    /** The text typed after the `@`. */
    text: string;
};

type Segment =
    | { type: 'text'; markupStart: number; markupEnd: number; plainStart: number; plainEnd: number }
    | {
          type: 'mention';
          markupStart: number;
          markupEnd: number;
          plainStart: number;
          plainEnd: number;
          display: string;
          id: string;
      };

/** Serialises a single mention into the wire format. */
export function formatMentionMarkup(display: string, id: string): string {
    return `@[${display}](${id})`;
}

function splitIntoSegments(markup: string): Segment[] {
    const segments: Segment[] = [];
    let markupIndex = 0;
    let plainIndex = 0;

    // `matchAll` needs a fresh lastIndex, `MENTION_TOKEN` is global and shared.
    MENTION_TOKEN.lastIndex = 0;
    for (const match of markup.matchAll(MENTION_TOKEN)) {
        const [token, display, id] = match;
        const tokenStart = match.index ?? 0;

        // The (possibly empty) text in front of the mention. Empty text segments matter: they are
        // where the caret sits when it is between two adjacent mentions.
        const text = markup.slice(markupIndex, tokenStart);
        segments.push({
            type: 'text',
            markupStart: markupIndex,
            markupEnd: tokenStart,
            plainStart: plainIndex,
            plainEnd: plainIndex + text.length,
        });
        plainIndex += text.length;

        segments.push({
            type: 'mention',
            markupStart: tokenStart,
            markupEnd: tokenStart + token.length,
            plainStart: plainIndex,
            plainEnd: plainIndex + display.length,
            display,
            id,
        });
        plainIndex += display.length;
        markupIndex = tokenStart + token.length;
    }

    const rest = markup.slice(markupIndex);
    segments.push({
        type: 'text',
        markupStart: markupIndex,
        markupEnd: markup.length,
        plainStart: plainIndex,
        plainEnd: plainIndex + rest.length,
    });

    return segments;
}

/** Turns the stored markup into the text the user sees, replacing each mention by its display. */
export function toPlainText(markup: string): string {
    MENTION_TOKEN.lastIndex = 0;
    return markup.replace(MENTION_TOKEN, (_token, display: string) => display);
}

/** Extracts every mention in the markup, in order of appearance. */
export function extractMentions(markup: string): Array<{ display: string; id: string }> {
    return splitIntoSegments(markup)
        .filter((segment): segment is Extract<Segment, { type: 'mention' }> => segment.type === 'mention')
        .map(({ display, id }) => ({ display, id }));
}

/**
 * Translates an index in the plain text into an index in the markup.
 *
 * Mentions are atomic: an index that falls inside a mention has no exact markup counterpart, so the
 * caller says what should happen — snap to the start of the token, snap past its end, or report
 * `null` so the caller can tell that the edit touched a mention.
 */
export function mapPlainIndexToMarkupIndex(
    markup: string,
    plainIndex: number,
    mode: 'start' | 'end' | 'null',
): number | null {
    for (const segment of splitIntoSegments(markup)) {
        if (segment.type === 'text') {
            if (segment.plainEnd >= plainIndex) {
                return segment.markupStart + (plainIndex - segment.plainStart);
            }
        } else if (segment.plainEnd > plainIndex) {
            switch (mode) {
                case 'null':
                    return null;
                case 'start':
                    return segment.markupStart;
                case 'end':
                    return segment.markupEnd;
            }
        }
    }
    return markup.length;
}

/**
 * Applies an edit made in the plain text back onto the markup.
 *
 * The textarea only tells us the new text and where the caret ended up, which is enough: a single
 * edit replaces one contiguous range. If that range overlaps a mention, the whole mention is
 * dropped — a half-deleted mention would be neither valid markup nor what the user meant.
 *
 * @param markup the current markup, i.e. the value before the edit
 * @param newPlainText the textarea value after the edit
 * @param caret `selectionStart` after the edit
 */
export function applyPlainTextChange(
    markup: string,
    newPlainText: string,
    caret: number,
): { markup: string; plainText: string; caret: number } {
    const oldPlainText = toPlainText(markup);
    const lengthDelta = oldPlainText.length - newPlainText.length;

    // Everything after the caret is untouched, so the replaced range in the old text ends here.
    let spliceStart = 0;
    const spliceEndCandidate = caret + lengthDelta;
    const maxPrefix = Math.min(caret, spliceEndCandidate);
    while (spliceStart < maxPrefix && oldPlainText[spliceStart] === newPlainText[spliceStart]) {
        spliceStart++;
    }
    const spliceEnd = Math.max(spliceStart, spliceEndCandidate);
    const inserted = newPlainText.slice(spliceStart, caret);

    const markupStart = mapPlainIndexToMarkupIndex(markup, spliceStart, 'start') ?? 0;
    const markupEnd = mapPlainIndexToMarkupIndex(markup, spliceEnd, 'end') ?? markup.length;

    return spliceMarkup(markup, markupStart, markupEnd, inserted);
}

/**
 * Replaces the mention trigger (the `@` plus the query the user typed) by a mention, followed by a
 * space so the user can keep typing.
 */
export function insertMention(
    markup: string,
    query: MentionQuery,
    caret: number,
    mention: { display: string; id: string },
): { markup: string; plainText: string; caret: number } {
    const markupStart = mapPlainIndexToMarkupIndex(markup, query.start, 'start') ?? 0;
    const markupEnd = mapPlainIndexToMarkupIndex(markup, caret, 'end') ?? markup.length;

    return spliceMarkup(
        markup,
        markupStart,
        markupEnd,
        formatMentionMarkup(mention.display, mention.id) + ' ',
        mention.display.length + 1,
    );
}

function spliceMarkup(
    markup: string,
    markupStart: number,
    markupEnd: number,
    inserted: string,
    /** Plain-text length of `inserted`, when it differs (i.e. when inserting markup). */
    insertedPlainLength: number = inserted.length,
): { markup: string; plainText: string; caret: number } {
    const newMarkup = markup.slice(0, markupStart) + inserted + markup.slice(markupEnd);
    return {
        markup: newMarkup,
        plainText: toPlainText(newMarkup),
        caret: toPlainText(markup.slice(0, markupStart)).length + insertedPlainLength,
    };
}

/**
 * Finds the mention the user is currently typing, or `null` when the caret is not behind a trigger.
 */
export function findMentionQuery(plainText: string, caret: number): MentionQuery | null {
    const match = plainText.slice(0, caret).match(MENTION_TRIGGER);
    if (!match) {
        return null;
    }
    const query = match[1];
    return { start: caret - query.length - 1, text: query };
}

/** Case- and accent-insensitive `includes`, used to filter the mention suggestions. */
export function matchesQuery(candidate: string, query: string): boolean {
    return normalize(candidate).includes(normalize(query));
}

function normalize(str: string): string {
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
}
